import React, { useState, useEffect, useRef } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const emptyStop = () => ({ customer: '', address: '', items: '', status: 'Pending' })

export default function Logistics() {
  const { user, profile, canEditData } = useAuth()
  const [tab, setTab] = useState('deliveries')
  const [deliveries, setDeliveries] = useState([])
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [uploadingNote, setUploadingNote] = useState(null)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const [form, setForm] = useState({
    delivery_date: ugandaDate(),
    driver: profile?.full_name || '',
    vehicle_reg: '',
    invoice_id: '',
    stops: [emptyStop()],
    km_driven: '',
    notes: '',
    checks: { fuel:false, clean:false, ice_packs:false, bags:false, dockets:false, phone:false, tyres:false, licence:false }
  })

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const [delRes, invRes] = await Promise.all([
      supabase.from('delivery_notes')
        .select('*, invoices(invoice_number, customer_name), profiles(full_name)')
        .order('delivery_date', { ascending: false }).limit(50),
      supabase.from('invoices')
        .select('id, invoice_number, customer_name, balance_due')
        .in('status', ['Unpaid','Partial'])
        .order('invoice_date', { ascending: false })
    ])
    setDeliveries(delRes.data || [])
    setInvoices(invRes.data || [])
    setLoading(false)
  }

  const setStop = (i, field, val) => {
    const stops = [...form.stops]
    stops[i] = { ...stops[i], [field]: val }
    setForm(f => ({ ...f, stops }))
  }

  const toggleCheck = (key) => setForm(f => ({ ...f, checks: { ...f.checks, [key]: !f.checks[key] } }))
  const allChecked = Object.values(form.checks).every(Boolean)

  const nextDNNum = () => {
    const nums = deliveries.map(d => parseInt(d.dn_number?.replace(/\D/g,''))).filter(Boolean)
    const next = nums.length ? Math.max(...nums) + 1 : 1
    return `DN-${new Date().getFullYear()}-${String(next).padStart(3,'0')}`
  }

  const handleSave = async () => {
    if (!form.driver) { setMsg({ type:'error', text:'Driver name is required.' }); return }
    setSaving(true); setMsg(null)
    const dn_number = nextDNNum()
    const { error } = await supabase.from('delivery_notes').insert({
      user_id: user.id, dn_number,
      delivery_date: form.delivery_date, driver: form.driver,
      vehicle_reg: form.vehicle_reg, invoice_id: form.invoice_id || null,
      stops: form.stops.filter(s => s.customer),
      km_driven: form.km_driven || null, notes: form.notes,
      checks: form.checks, status: 'Pending',
    })
    if (error) { setMsg({ type:'error', text:error.message }); setSaving(false); return }
    setMsg({ type:'success', text:`Delivery Note ${dn_number} created. Print it for the driver.` })
    setSaving(false); setShowForm(false); loadData()
    setTimeout(() => setMsg(null), 5000)
  }

  const handlePrint = (dn) => {
    const inv = dn.invoices
    const w = window.open('','_blank')
    w.document.write(`<html><head><title>Delivery Note ${dn.dn_number}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:32px;color:#1C3520;}
      h1{font-size:1.4rem;margin-bottom:4px;}
      .sub{color:#666;font-size:0.85rem;margin-bottom:24px;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      th{background:#1C3520;color:white;padding:8px 12px;text-align:left;font-size:0.82rem;}
      td{padding:8px 12px;border-bottom:1px solid #ddd;font-size:0.85rem;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px;}
      .label{font-size:0.72rem;text-transform:uppercase;letter-spacing:0.1em;color:#666;margin-bottom:3px;}
      .val{font-weight:600;}
      .sig-box{border:2px dashed #ccc;height:80px;margin-top:8px;border-radius:6px;}
      .footer{margin-top:40px;font-size:0.75rem;color:#888;text-align:center;}
      @media print{body{padding:16px;}}
    </style></head><body>
    <h1>🚚 DELIVERY NOTE — ${dn.dn_number}</h1>
    <div class="sub">Kobflo Greentek Ltd · app.kobfloagri.com</div>
    <div class="grid">
      <div><div class="label">Date</div><div class="val">${new Date(dn.delivery_date).toLocaleDateString('en-UG')}</div></div>
      <div><div class="label">Driver</div><div class="val">${dn.driver}</div></div>
      <div><div class="label">Vehicle</div><div class="val">${dn.vehicle_reg||'—'}</div></div>
      <div><div class="label">Invoice Ref</div><div class="val">${inv?.invoice_number||'—'}</div></div>
      <div><div class="label">Customer</div><div class="val">${inv?.customer_name||(dn.stops?.[0]?.customer||'—')}</div></div>
    </div>
    <table>
      <thead><tr><th>Customer / Stop</th><th>Address</th><th>Items Delivered</th></tr></thead>
      <tbody>${(dn.stops||[]).map(s=>`<tr><td>${s.customer}</td><td>${s.address}</td><td>${s.items}</td></tr>`).join('')}</tbody>
    </table>
    <div class="grid">
      <div><div class="label">Driver Signature</div><div class="sig-box"></div>
      <div style="font-size:0.75rem;color:#888;margin-top:4px;">Name: ${dn.driver} &nbsp;&nbsp; Date: ________</div></div>
      <div><div class="label">Received By (Customer Signature)</div><div class="sig-box"></div>
      <div style="font-size:0.75rem;color:#888;margin-top:4px;">Name: ________________ &nbsp;&nbsp; Date: ________</div></div>
    </div>
    <div class="footer">This delivery note confirms goods were received in good condition. Queries within 24 hours. · Kobflo Greentek Ltd</div>
    </body></html>`)
    w.document.close(); w.print()
  }

  const handleUploadNote = async (deliveryId, file) => {
    if (!file) return
    setUploadingNote(deliveryId)
    const ext = file.name.split('.').pop()
    const path = `delivery-notes/${deliveryId}_${Date.now()}.${ext}`
    const { error: uploadErr } = await supabase.storage.from('logistics').upload(path, file)
    if (uploadErr) { setMsg({ type:'error', text:'Upload failed: '+uploadErr.message }); setUploadingNote(null); return }
    const { data: { publicUrl } } = supabase.storage.from('logistics').getPublicUrl(path)
    await supabase.from('delivery_notes').update({ signed_note_url: publicUrl, status:'Delivered' }).eq('id', deliveryId)
    setUploadingNote(null)
    setMsg({ type:'success', text:'Signed delivery note uploaded. Status updated to Delivered.' })
    loadData(); setTimeout(() => setMsg(null), 4000)
  }

  const VEHICLE_CHECKS = [
    { key:'fuel', label:'Fuel adequate for planned route' },
    { key:'clean', label:'Vehicle interior clean — no chemicals near produce' },
    { key:'ice_packs', label:'Ice packs frozen and ready' },
    { key:'bags', label:'Insulated bags/boxes loaded' },
    { key:'dockets', label:'All orders packed, labelled, dockets ready' },
    { key:'phone', label:'Phone charged — GPS/maps ready' },
    { key:'tyres', label:'Tyres, lights, windscreen OK' },
    { key:'licence', label:"Driver's licence on person" },
  ]

  const statusColor = { Pending:'badge-amber', Delivered:'badge-green', Failed:'badge-red' }

  return (
    <AppLayout title="Logistics">
      <div className="section-header">
        <div className="section-icon">🚚</div>
        <div className="section-info">
          <h2>Logistics & Deliveries</h2>
          <p>Delivery notes, driver checklists, and signed note uploads.</p>
        </div>
        {canEditData && (
          <button className="btn btn-primary" style={{ marginLeft:'auto' }} onClick={() => setShowForm(true)}>
            + New Delivery Note
          </button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      <div className="tabs">
        <button className={`tab ${tab==='deliveries'?'active':''}`} onClick={() => setTab('deliveries')}>🚚 Delivery Notes</button>
        <button className={`tab ${tab==='inbound'?'active':''}`} onClick={() => setTab('inbound')}>📥 Receiving Supplies</button>
        <button className={`tab ${tab==='coldchain'?'active':''}`} onClick={() => setTab('coldchain')}>❄️ Cold Chain Rules</button>
      </div>

      {/* New Delivery Note Form */}
      {showForm && tab==='deliveries' && (
        <div className="card" style={{ marginBottom:16, border:'2px solid var(--amber)' }}>
          <div className="card-title">New Delivery Note</div>
          <div className="card-subtitle">Fill in details, save, then print for the driver. Driver uploads signed copy on return.</div>

          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Delivery Date</label><input className="form-input" type="date" value={form.delivery_date} onChange={e => setForm(f=>({...f,delivery_date:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Driver</label><input className="form-input" value={form.driver} onChange={e => setForm(f=>({...f,driver:e.target.value}))} placeholder="Driver name" /></div>
            <div className="form-group"><label className="form-label">Vehicle Reg</label><input className="form-input" value={form.vehicle_reg} onChange={e => setForm(f=>({...f,vehicle_reg:e.target.value}))} placeholder="e.g. UAZ 123A" /></div>
            <div className="form-group">
              <label className="form-label">Linked Invoice (optional)</label>
              <select className="form-select" value={form.invoice_id} onChange={e => setForm(f=>({...f,invoice_id:e.target.value}))}>
                <option value="">— Select invoice —</option>
                {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoice_number} — {inv.customer_name} (UGX {(inv.balance_due||0).toLocaleString()})</option>)}
              </select>
            </div>
          </div>

          <div style={{ fontWeight:700, marginBottom:10 }}>🔍 Pre-Departure Vehicle Checks</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
            {VEHICLE_CHECKS.map(({ key, label }) => (
              <div key={key} onClick={() => toggleCheck(key)} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:8, border:`1.5px solid ${form.checks[key]?'var(--green-bright)':'var(--border)'}`, background:form.checks[key]?'var(--green-pale)':'white', cursor:'pointer', fontSize:'0.82rem', transition:'all 0.15s' }}>
                <span style={{ fontSize:'1.1rem' }}>{form.checks[key]?'✅':'⬜'}</span>{label}
              </div>
            ))}
          </div>
          {!allChecked && <div className="alert alert-amber" style={{ marginBottom:12 }}>⚠️ Complete all vehicle checks before departing.</div>}

          <div style={{ fontWeight:700, marginBottom:10 }}>📦 Delivery Stops</div>
          <div className="table-wrap" style={{ marginBottom:8 }}>
            <table>
              <thead><tr><th>Customer</th><th>Address</th><th>Items</th><th style={{width:32}}></th></tr></thead>
              <tbody>
                {form.stops.map((stop,i) => (
                  <tr key={i}>
                    <td><input className="form-input" placeholder="Customer name" value={stop.customer} onChange={e => setStop(i,'customer',e.target.value)} /></td>
                    <td><input className="form-input" placeholder="Delivery address" value={stop.address} onChange={e => setStop(i,'address',e.target.value)} /></td>
                    <td><input className="form-input" placeholder="e.g. 3kg Lettuce" value={stop.items} onChange={e => setStop(i,'items',e.target.value)} /></td>
                    <td>{form.stops.length>1 && <button className="btn-del-row" onClick={() => setForm(f=>({...f,stops:f.stops.filter((_,j)=>j!==i)}))}>✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary btn-sm" style={{ marginBottom:16 }} onClick={() => setForm(f=>({...f,stops:[...f.stops,emptyStop()]}))}>+ Add Stop</button>

          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Total KM Driven</label><input className="form-input" type="number" placeholder="km" value={form.km_driven} onChange={e => setForm(f=>({...f,km_driven:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Notes</label><input className="form-input" placeholder="Any special instructions" value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} /></div>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner"/>Saving...</>:'💾 Save Delivery Note'}</button>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {tab==='deliveries' && (
        <div className="card">
          <div className="card-title">Delivery Notes</div>
          <div className="card-subtitle">Print delivery notes for drivers. After delivery, upload the signed physical copy using Camera or Upload.</div>

          {/* Hidden file inputs */}
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display:'none' }}
            onChange={e => { if(e.target.files[0]&&uploadingNote) handleUploadNote(uploadingNote,e.target.files[0]); e.target.value='' }} />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }}
            onChange={e => { if(e.target.files[0]&&uploadingNote) handleUploadNote(uploadingNote,e.target.files[0]); e.target.value='' }} />

          {loading ? (
            <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          ) : deliveries.length===0 ? (
            <div className="empty-state"><div className="empty-icon">🚚</div><h3>No delivery notes yet</h3><p>Create one when you're ready to dispatch goods.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>DN #</th><th>Date</th><th>Driver</th><th>Invoice</th><th>Customer</th><th>Status</th><th>Signed Note</th><th>Actions</th></tr></thead>
                <tbody>
                  {deliveries.map(dn => (
                    <tr key={dn.id}>
                      <td style={{ fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{dn.dn_number}</td>
                      <td style={{ fontSize:'0.78rem' }}>{new Date(dn.delivery_date).toLocaleDateString('en-UG')}</td>
                      <td>{dn.driver}</td>
                      <td style={{ fontSize:'0.78rem', fontFamily:"'DM Mono',monospace" }}>{dn.invoices?.invoice_number||'—'}</td>
                      <td>{dn.invoices?.customer_name||dn.stops?.[0]?.customer||'—'}</td>
                      <td><span className={`badge ${statusColor[dn.status]||'badge-grey'}`}>{dn.status}</span></td>
                      <td>
                        {dn.signed_note_url
                          ? <a href={dn.signed_note_url} target="_blank" rel="noreferrer" style={{ color:'var(--green-mid)', fontWeight:600, fontSize:'0.78rem' }}>📎 View</a>
                          : <span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>Not uploaded</span>
                        }
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => handlePrint(dn)}>🖨️ Print</button>
                          {!dn.signed_note_url && (
                            <>
                              <button className="btn btn-secondary btn-sm" style={{ color:'var(--blue)' }}
                                onClick={() => { setUploadingNote(dn.id); cameraInputRef.current.click() }}>📷 Camera</button>
                              <button className="btn btn-secondary btn-sm"
                                onClick={() => { setUploadingNote(dn.id); fileInputRef.current.click() }}>📁 Upload</button>
                            </>
                          )}
                          {uploadingNote===dn.id && <span className="spinner dark" style={{ width:16,height:16 }} />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab==='inbound' && (
        <div className="card">
          <div className="card-title">Receiving Supplies — SOP</div>
          {[
            ['1','Check Against Purchase Order','Have your original Purchase Order ready before the supplier arrives. Count every item received. Check product names, sizes, and quantities match exactly.'],
            ['2','Inspect Quality','Check for: damaged packaging, wrong products, missing items, expired dates on chemicals and nutrients. Photograph any issues before signing.'],
            ['3','Sign or Reject','If everything is correct — sign the delivery note and keep your copy. If there are problems — write the issues clearly on the delivery note BEFORE signing. Contact supplier within 24 hours for any claims.'],
            ['4','Store Immediately','Perishables first. Store chemicals in the locked, ventilated chemical store — AWAY from food and growing areas. Label everything with the date received.'],
            ['5','Update Stock Records','Record the receipt in Inventory & Stores. Use the Stock In button for each item received. Match the invoice to the purchase order. Pass invoice to finance.'],
          ].map(([num,title,desc]) => (
            <div key={num} style={{ display:'flex', gap:16, marginBottom:16 }}>
              <div style={{ width:32,height:32,minWidth:32,background:'var(--amber)',color:'white',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'DM Mono',monospace",fontSize:'0.8rem',fontWeight:600 }}>{num}</div>
              <div><div style={{ fontWeight:700, marginBottom:3 }}>{title}</div><div style={{ fontSize:'0.82rem', color:'var(--text-muted)', lineHeight:1.6 }}>{desc}</div></div>
            </div>
          ))}
        </div>
      )}

      {tab==='coldchain' && (
        <div className="card">
          <div className="card-title">Cold Chain Rules</div>
          <div className="alert alert-red">🚨 A broken cold chain can cause food spoilage or illness. Never compromise on this.</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Stage</th><th>Temperature</th><th>Maximum Time</th><th>Action if Breached</th></tr></thead>
              <tbody>
                {[
                  ['Post-harvest (farm)','Move to fridge within 30 min','30 minutes at ambient','Rush to fridge immediately'],
                  ['Farm fridge','2–4°C','Lettuce: 5 days | Strawberries: 3 days','Check fridge — call engineer if faulty'],
                  ['Loading vehicle','Load last-minute with ice packs','Do not leave in car waiting','Keep in fridge until last moment'],
                  ['In transit','Keep below 8°C with ice packs','Max 2 hours without active cooling','Do not deliver — return to fridge'],
                  ['Customer handover','Hand directly or leave in cool spot','Never leave in direct sun','Phone customer to confirm receipt'],
                ].map(([stage,temp,time,action]) => (
                  <tr key={stage}>
                    <td style={{ fontWeight:600 }}>{stage}</td>
                    <td style={{ fontSize:'0.8rem' }}>{temp}</td>
                    <td style={{ fontSize:'0.8rem', color:'var(--amber)' }}>{time}</td>
                    <td style={{ fontSize:'0.8rem', color:'var(--red)' }}>{action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="alert alert-amber" style={{ marginTop:14 }}>If produce has been above 8°C for more than 2 hours — do not sell it. Record the incident and dispose safely. This is non-negotiable.</div>
        </div>
      )}
    </AppLayout>
  )
}
